/**
 * Tracker Transfers tab — read-only modal showing the transfer request QR for a row.
 *
 * Fetches the transfer QuestionnaireResponse for the episode and reuses the
 * shared TransferDetailModal to render reasons, explanation, and preferences.
 */

import type { QuestionnaireResponse, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import { getTransferQRForEpisode } from '../../../lib/transferService';
import { TransferDetailModal } from '../../caseload/components/TransferDetailModal';
import type { EpisodeRow } from '../types/lifecycle';

export interface TransferRequestModalProps {
  opened: boolean;
  onClose: () => void;
  episode: EpisodeRow | null;
  serviceRequest?: ServiceRequest;
}

export function TransferRequestModal({
  opened,
  onClose,
  episode,
  serviceRequest,
}: TransferRequestModalProps): JSX.Element {
  const medplum = useMedplum();
  const [qr, setQr] = useState<QuestionnaireResponse | undefined>();

  useEffect(() => {
    if (!opened || !episode?.episodeOfCareId) {
      setQr(undefined);
      return;
    }
    let cancelled = false;
    void getTransferQRForEpisode(medplum, episode.episodeOfCareId, {
      patientId: episode.clientId,
    })
      .then((result) => {
        if (!cancelled) setQr(result);
      })
      .catch(() => {
        if (!cancelled) setQr(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [opened, episode?.episodeOfCareId, episode?.clientId, medplum]);

  return (
    <TransferDetailModal
      opened={opened}
      onClose={onClose}
      clientName={episode?.clientName}
      episodeDisplay={episode?.episodeServiceType}
      status={episode?.stage}
      fromProvider={episode?.fromProvider}
      paymentType={episode?.payer}
      submissionDate={episode?.transferRequestedAt}
      questionnaireResponse={qr}
      serviceRequest={serviceRequest}
    />
  );
}
